import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import MenuItem from '@material-ui/core/MenuItem';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';

import * as statisticsReducers from '../reducer';
import * as statisticsActionMgr from '../action-mgr';
import { getUserId } from '../../helpers/common';

const styles = {
  CheckboxFormLabelCountry: { textAlign: 'left', padding: '0px 2px', fontSize: '13px', width: '55px' },
  CheckboxFormLabelCity: { textAlign: 'left', padding: '0px 2px', fontSize: '13px', width: '30px' },
  CheckboxFormLabelProject: { textAlign: 'left', padding: '0px 2px', fontSize: '13px', width: '50px' },

  formControlWrapper: { marginBottom: 15 },
  formControlWrapperSelectMenu: {
    '& > div > div': {
      boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    },
  },
  formControlWrapperCheckbox: {
    width: '100%',
    marginBottom: 10,
    paddingLeft: '10px',
    border: '1px solid #0000003d',
    borderRadius: '5px',
    boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    maxHeight: '418px', // Limit height 
    overflowY: 'auto', // Enable vertical scrolling
    // Thin custom scrollbar
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f0f0f0',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: '4px',
      border: '2px solid #f0f0f0'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    }
  },
  scrollContainer: {
    maxHeight: 'calc(100vh - 500px)',           // Limit height to trigger scroll
    overflowY: 'auto',            // Enable vertical scrolling
    scrollBehavior: 'smooth',     // Smooth scrolling effect
    
    /* Custom Scrollbar for WebKit Browsers */
    '&::-webkit-scrollbar': {
      width: '6px',               // Narrow scrollbar width
    },
    '&::-webkit-scrollbar-track': {
      background: '#f0f0f0',      // Light gray background
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',    // Darker scroll thumb
      borderRadius: '4px',
      border: '2px solid #f0f0f0'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',         // Darker on hover
    },
  }

};

const formatEnergyYield = (value) => {
  const numericValue = Number(value);
  const displayValue = numericValue >= 1000000 ? numericValue / 1000 : numericValue;

  return Number.isFinite(numericValue)
    ? displayValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: numericValue >= 1000000 ? 3 : 2,
      })
    : value ?? '';
};

const getEnergyYieldUnit = (value) => Number(value) >= 1000000 ? 'MWh' : 'kWh';

const formatCsvRow = (row) => row.map((value) => {
  const cell = String(value ?? '');
  return /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}).join(',');

class StationStatisticsPortfolioForm extends Component {

  constructor(props) {
    super(props);
    const { portfolioDetails } = props;

    this.state = {
      selectedPortfolioLevel: 'country',
      selectedCountries: portfolioDetails.countryList || [],
      selectAllCountries: (portfolioDetails.countryList || []).length > 0,
      selectedCities: portfolioDetails.cityList || [],
      selectAllCities: (portfolioDetails.cityList || []).length > 0,
      selectedProjects: portfolioDetails.projectList || [],
      selectAllProjects: (portfolioDetails.projectList || []).length > 0,
      isUserInteractingWithCountry: false,
      hasInitialLoadCompleted: false, // New flag to control initial API calls
    };
  }

  isDownloadReady = () => {
    const { portfolioData, portfolioStatisticsData, selectedPortfolioYear } = this.props;
    const stats = portfolioStatisticsData || [];

    // Check if portfolio data is valid
    const hasPortfolio = !this.isPortfolioDataInvalid(portfolioData);
    
    // Check if we have statistics data
    const hasStats = stats.length > 0;

    // Check granularity: if a specific year is selected, ensure we have stats for that year
    // If year is "all", we should have stats (any year is fine)
    // If year is specific, we need at least one stat that matches that year
    let wrongGranularity = false;
    if (selectedPortfolioYear !== "all" && hasStats) {
      // Check if we have at least one stat that matches the selected year
      const hasMatchingYear = stats.some(s => {
        const statTime = String(s.time || '');
        const selectedYear = String(selectedPortfolioYear);
        return statTime.includes(selectedYear);
      });
      wrongGranularity = !hasMatchingYear;
    }

    return hasPortfolio && hasStats && !wrongGranularity;
  };

  componentDidMount() {
  const {
    fetchPortfolioLevels,
    callPortfolioTotalize,
    fetchPortfolioDetails,
    callPortfolioData,
    callPortfolioStatisticsData,
    setSelectedPortfolioCountry,
    selectedPortfolioCompany,
    portfolioTotalize,
  } = this.props;

  const userId = getUserId();
  const companyName = selectedPortfolioCompany || portfolioTotalize.companyName;

  // 1) Load levels and set default level (keep original behavior)
  const levelsPromise = fetchPortfolioLevels().then(() => {
    const { portfolioLevels } = this.props;
    const defaultLevel = portfolioLevels.find(
      (level) => level.portfolioSelection.toLowerCase() === 'country'
    );
    if (defaultLevel) {
      this.setState({ selectedPortfolioLevel: defaultLevel.portfolioSelection.toLowerCase() });
    }
  });

  // 2) Load totalize (keep original behavior)
  const totalizeRequest = { userId, ...(companyName && { company: companyName }) };
  callPortfolioTotalize(totalizeRequest);

  // 3) Bootstrap initial filters/data so UI is ready on first paint
  (async () => {
    try {
      // Ensure levels are set before using selectedPortfolioLevel (but don’t block on totalize)
      await levelsPromise;

      const baseFilters = {
        userId,
        company: companyName ? [companyName] : [],
        country: ['all'],
        city: ['all'],
        project: ['all'],
      };

      // 3a) Load lists (companies/countries/cities/projects/stations)
      const details = await fetchPortfolioDetails(baseFilters);

      // 3b) Auto-select all available countries (mirrors your manual “All → country” action)
      const countries = details?.countryList || [];
      if (countries.length) {
        this.setState({
          selectedCountries: countries,
          selectAllCountries: true,
          isUserInteractingWithCountry: false,
        });
        setSelectedPortfolioCountry(countries); // keep Redux in sync
      }

      // 3c) Load portfolio summary/details for current (or default) level across ALL years
      const level = (this.state.selectedPortfolioLevel || 'country').toLowerCase();
      await callPortfolioData({
        ...baseFilters,
        country: countries.length ? countries : ['all'], // use the auto-selected countries if present
        portfolioLevel: level,
        type: 'all',
      });

      // 3d) Load station statistics (ALL years) using station names from details
      const stationNames = details?.stationDetails?.map((s) => s.name) || [];
      if (stationNames.length) {
        await callPortfolioStatisticsData({
          stationNames,
          type: 'all',
        });
      }
    } finally {
      // 4) Mark initial load as complete AFTER bootstrap so componentDidUpdate logic won’t bail early
      this.setState({ hasInitialLoadCompleted: true });
    }
  })();
}

  // componentDidMount() {
  //   const {
  //     fetchPortfolioLevels,
  //     callPortfolioTotalize,
  //     selectedPortfolioCompany,
  //     portfolioTotalize,
  //   } = this.props;

  //   const userId = getUserId();
  //   const companyName = selectedPortfolioCompany || portfolioTotalize.companyName;

  //   fetchPortfolioLevels().then(() => {
  //     const { portfolioLevels } = this.props;
  //     const defaultLevel = portfolioLevels.find(
  //       (level) => level.portfolioSelection.toLowerCase() === 'country'
  //     );

  //     if (defaultLevel) {
  //       this.setState({ selectedPortfolioLevel: defaultLevel.portfolioSelection.toLowerCase() });
  //     }
  //   });

  //   const totalizeRequest = {
  //     userId,
  //     ...(companyName && { company: companyName }),
  //   };
  //   callPortfolioTotalize(totalizeRequest);

  //   // Set flag after initial API calls
  //   this.setState({
  //     hasInitialLoadCompleted: true,
  //   });
  // }

  // componentDidUpdate(prevProps, prevState) {
  //   const {
  //     portfolioData,
  //     portfolioDetails,
  //     setSelectedPortfolioCountry,
  //     selectedPortfolioCompany,
  //     selectedPortfolioCountry,
  //     selectedPortfolioCity,
  //     selectedPortfolioProject,
  //     selectedPortfolioYear,
  //     selectedPortfolioStations,
  //     callPortfolioData,
  //     callPortfolioStatisticsData,
  //     setSelectedPortfolioStations,
  //     callPortfolioTotalize,
  //     fetchPortfolioDetails,
  //   } = this.props;

  //   const userId = getUserId();
  //   const { 
  //     selectedPortfolioLevel, 
  //     selectedCountries, 
  //     isUserInteractingWithCountry, 
  //     hasInitialLoadCompleted, 
  //     isCompanyChanging  
  //   } = this.state;

  //   // Prevent duplicate API calls during company change
  //   if (isCompanyChanging) return;

  //   // Prevent auto-select if user interacted
  //   if (
  //     prevProps.portfolioDetails.countryList !== portfolioDetails.countryList &&
  //     selectedPortfolioLevel === 'country' &&
  //     portfolioDetails.countryList.length > 0 &&
  //     selectedCountries.length === 0 &&
  //     !isUserInteractingWithCountry
  //   ) {
  //     this.setState({
  //       selectedCountries: portfolioDetails.countryList,
  //       selectAllCountries: true,
  //     });

  //     setSelectedPortfolioCountry(portfolioDetails.countryList);
  //   }

  //   // Prevent API calls during initial load
  //   if (!hasInitialLoadCompleted) return;

  //   // Check if selections have changed
  //   const hasFilterChanged =
  //     prevProps.selectedPortfolioCompany !== selectedPortfolioCompany ||
  //     prevProps.selectedPortfolioCountry !== selectedPortfolioCountry ||
  //     prevProps.selectedPortfolioCity !== selectedPortfolioCity ||
  //     prevProps.selectedPortfolioProject !== selectedPortfolioProject ||
  //     prevProps.selectedPortfolioYear !== selectedPortfolioYear ||
  //     prevProps.selectedPortfolioStations !== selectedPortfolioStations ||
  //     prevState.selectedPortfolioLevel !== selectedPortfolioLevel;

  //   if (hasFilterChanged) {
  //     const formatAsArray = (value) => {
  //       if (Array.isArray(value)) {
  //         return value.length > 0 ? value : ['all'];
  //       }
  //       return value ? [value] : ['all'];
  //     };

  //     const portfolioRequest = {
  //       userId,
  //       company: formatAsArray(selectedPortfolioCompany),
  //       country: formatAsArray(selectedPortfolioCountry),
  //       city: formatAsArray(selectedPortfolioCity),
  //       project: formatAsArray(selectedPortfolioProject),
  //     };
  //     fetchPortfolioDetails(portfolioRequest);

  //     const requestData = {
  //       userId,
  //       company: formatAsArray(selectedPortfolioCompany),
  //       country: formatAsArray(selectedPortfolioCountry),
  //       city: formatAsArray(selectedPortfolioCity),
  //       project: formatAsArray(selectedPortfolioProject),
  //       portfolioLevel: selectedPortfolioLevel.toLowerCase(),
  //       type: selectedPortfolioYear === 'all' ? 'all' : 'year',
  //       ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear }),
  //     };
  //     callPortfolioData(requestData);
  //   }

  //   // Extract station names from portfolioDetails.stationDetails
  //   const stationNames = portfolioDetails?.stationDetails?.map(station => station.name) || [];
  //   const prevStationNames = prevProps.portfolioDetails?.stationDetails?.map(station => station.name) || [];

  //   // Call API if stationNames or selected year changes
  //   if (JSON.stringify(stationNames) !== JSON.stringify(prevStationNames) ||
  //   prevProps.selectedPortfolioYear !== selectedPortfolioYear) {
  //       const statisticsRequest = {
  //           stationNames,
  //           type: selectedPortfolioYear === 'all' ? 'all' : 'year',
  //           ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear })
  //       };
  //       callPortfolioStatisticsData(statisticsRequest);
  //   }

  // }

    componentDidUpdate(prevProps, prevState) {
    const {
      portfolioData,
      portfolioDetails,
      setSelectedPortfolioCountry,
      selectedPortfolioCompany,
      selectedPortfolioCountry,
      selectedPortfolioCity,
      selectedPortfolioProject,
      selectedPortfolioYear,
      selectedPortfolioStations,
      callPortfolioData,
      callPortfolioStatisticsData,
      setSelectedPortfolioStations,
      callPortfolioTotalize,
      fetchPortfolioDetails,
    } = this.props;

    const userId = getUserId();
    const {
      selectedPortfolioLevel,
      selectedCountries,
      isUserInteractingWithCountry,
      hasInitialLoadCompleted,
      isCompanyChanging,
    } = this.state;

    // Prevent duplicate API calls during company change
    if (isCompanyChanging) return;

    // Prevent auto-select if user interacted
    if (
      prevProps.portfolioDetails.countryList !== portfolioDetails.countryList &&
      selectedPortfolioLevel === 'country' &&
      portfolioDetails.countryList.length > 0 &&
      selectedCountries.length === 0 &&
      !isUserInteractingWithCountry
    ) {
      this.setState({
        selectedCountries: portfolioDetails.countryList,
        selectAllCountries: true,
      });

      setSelectedPortfolioCountry(portfolioDetails.countryList);
    }

    // Prevent API calls during initial load
    if (!hasInitialLoadCompleted) return;

    // ---- Helpers -------------------------------------------------------------
    // Helper to compare arrays by value
    const arraysEqual = (a, b) => {
      if (a === b) return true;
      if (!a || !b) return false;
      if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    // Helper to normalize value to array for comparison
    const normalizeToArray = (value) => {
      if (Array.isArray(value)) return value;
      if (!value || value === '') return [];
      return [value];
    };

    const formatAsArray = (value) => {
      if (Array.isArray(value)) return value.length > 0 ? value : ['all'];
      return value ? [value] : ['all'];
    };

    const buildRequestData = () => ({
      userId,
      company: formatAsArray(selectedPortfolioCompany),
      country: formatAsArray(selectedPortfolioCountry),
      city: formatAsArray(selectedPortfolioCity),
      project: formatAsArray(selectedPortfolioProject),
      portfolioLevel: (selectedPortfolioLevel || 'country').toLowerCase(),
      type: selectedPortfolioYear === 'all' ? 'all' : 'year',
      ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear }),
    });

    // ---- Existing: fetch on filter changes -----------------------------------
    // Compare arrays by value, not reference
    const prevCityArray = normalizeToArray(prevProps.selectedPortfolioCity);
    const currCityArray = normalizeToArray(selectedPortfolioCity);
    const prevProjectArray = normalizeToArray(prevProps.selectedPortfolioProject);
    const currProjectArray = normalizeToArray(selectedPortfolioProject);
    const prevCountryArray = normalizeToArray(prevProps.selectedPortfolioCountry);
    const currCountryArray = normalizeToArray(selectedPortfolioCountry);

    const hasFilterChanged =
      prevProps.selectedPortfolioCompany !== selectedPortfolioCompany ||
      !arraysEqual(prevCountryArray, currCountryArray) ||
      !arraysEqual(prevCityArray, currCityArray) ||
      !arraysEqual(prevProjectArray, currProjectArray) ||
      prevProps.selectedPortfolioYear !== selectedPortfolioYear ||
      !arraysEqual(normalizeToArray(prevProps.selectedPortfolioStations), normalizeToArray(selectedPortfolioStations)) ||
      prevState.selectedPortfolioLevel !== selectedPortfolioLevel;

    // Prevent API calls when selections are explicitly empty (user unchecked "All")
    // Only allow API calls if required filters for the current level have valid values
    const hasValidFilters = (() => {
      if (!selectedPortfolioCompany) return false;
      
      // For country level: need at least countries selected
      if (selectedPortfolioLevel === 'country') {
        return currCountryArray.length > 0;
      }
      
      // For city level: need countries and cities selected
      if (selectedPortfolioLevel === 'city') {
        return currCountryArray.length > 0 && currCityArray.length > 0;
      }
      
      // For project level: need countries, cities, and projects selected
      if (selectedPortfolioLevel === 'project') {
        return currCountryArray.length > 0 && currCityArray.length > 0 && currProjectArray.length > 0;
      }
      
      return true; // Default to true for unknown levels
    })();

    if (hasFilterChanged && hasValidFilters) {
      const portfolioRequest = {
        userId,
        company: formatAsArray(selectedPortfolioCompany),
        country: formatAsArray(selectedPortfolioCountry),
        city: formatAsArray(selectedPortfolioCity),
        project: formatAsArray(selectedPortfolioProject),
      };
      fetchPortfolioDetails(portfolioRequest);

      const requestData = buildRequestData();
      callPortfolioData(requestData);
    }

    // ---- NEW: Rehydrate when returning and the data slice is empty -----------
    // Consider the component "active" if no isActive prop is provided.
    const isActiveNow = this.props.isActive === undefined ? true : !!this.props.isActive;
    const becameActive =
      prevProps.isActive === false && this.props.isActive === true;

    const portfolioEmpty =
      !portfolioData ||
      !Array.isArray(portfolioData.portfolioSummary) ||
      portfolioData.portfolioSummary.length === 0 ||
      !Array.isArray(portfolioData.portfolioDetails) ||
      portfolioData.portfolioDetails.length === 0;

    if (
      !hasFilterChanged &&           // only when filters didn't change
      isActiveNow &&                 // tab is visible/active (or no prop provided)
      (becameActive || portfolioEmpty) && // we just became active OR data is empty
      hasValidFilters &&             // we have a usable filter set
      !isCompanyChanging             // avoid racing company change
    ) {
      const requestData = buildRequestData();
      callPortfolioData(requestData);

      // Also ensure station stats are (re)hydrated with current station names
      const stationNames =
        portfolioDetails?.stationDetails?.map((s) => s.name) || [];
      if (stationNames.length) {
        const statsReq = {
          stationNames,
          type: selectedPortfolioYear === 'all' ? 'all' : 'year',
          ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear }),
        };
        callPortfolioStatisticsData(statsReq);
      }
    }

    // ---- Existing: (re)fetch statistics when stationNames or year changes ----
    const stationNamesNow =
      portfolioDetails?.stationDetails?.map((station) => station.name) || [];
    const prevStationNames =
      prevProps.portfolioDetails?.stationDetails?.map((station) => station.name) || [];

    // Compare station names arrays by value, not reference
    const stationNamesChanged = !arraysEqual(
      [...stationNamesNow].sort(),
      [...prevStationNames].sort()
    );
    const yearChanged = prevProps.selectedPortfolioYear !== selectedPortfolioYear;

    // Only call statistics API if:
    // 1. Station names actually changed (by value, not reference)
    // 2. Year changed
    // 3. We have valid station names (not empty)
    // 4. We have valid filters (prevent calls when all selections are empty)
    if (
      (stationNamesChanged || yearChanged) &&
      stationNamesNow.length > 0 &&
      hasValidFilters
    ) {
      const statisticsRequest = {
        stationNames: stationNamesNow,
        type: selectedPortfolioYear === 'all' ? 'all' : 'year',
        ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear }),
      };
      callPortfolioStatisticsData(statisticsRequest);
    }
  }

  // method to determine visible checkboxes based on the selected portfolio level
  getVisibleCheckboxes = () => {
    const { selectedPortfolioLevel, selectAllCountries, selectAllCities, selectedCountries, selectedCities } = this.state;
  
    const hasSelectedCountry = selectedCountries.length > 0;
    const hasSelectedCity = selectedCities.length > 0;
  
    return {
      showCountry: selectedPortfolioLevel === 'country' || selectedPortfolioLevel === 'city' || selectedPortfolioLevel === 'project',
      showCity: (selectedPortfolioLevel === 'city' || selectedPortfolioLevel === 'project') &&
                (selectAllCountries || hasSelectedCountry),
      showProject: selectedPortfolioLevel === 'project' &&
                   (selectAllCountries || hasSelectedCountry) &&
                   (selectAllCities || hasSelectedCity),
    };
  };
  
  // Update handlePortfolioLevelChange to reset selections when switching levels
  handlePortfolioLevelChange = (event) => {
    const selectedLevel = event.target.value;
    const {
      portfolioDetails,
      setSelectedPortfolioCountry,
      setSelectedPortfolioCity,
      setSelectedPortfolioProject,
      callPortfolioData,
      callPortfolioStatisticsData,
      selectedPortfolioCompany,
      portfolioTotalize,
      selectedPortfolioYear
    } = this.props;

    const userId = getUserId();
    const companyName = selectedPortfolioCompany || portfolioTotalize.companyName;

    this.setState({
      selectedPortfolioLevel: selectedLevel,
      selectedCountries: [],
      selectAllCountries: false,
      selectedCities: [],
      selectAllCities: false,
      selectedProjects: [],
      selectAllProjects: false,
    });

    if (selectedLevel.toLowerCase() === 'country') {
      const countries = portfolioDetails.countryList || [];
      this.setState({
        selectedCountries: countries,
        selectAllCountries: countries.length > 0,
      });
      setSelectedPortfolioCountry(countries);

      callPortfolioData({
        userId,
        company: companyName ? [companyName] : [],
        country: countries.length > 0 ? countries : ['all'],
        city: ['all'],
        project: ['all'],
        portfolioLevel: 'country',
        type: 'all',
      });
    } else if (selectedLevel.toLowerCase() === 'city') {
      const countries = portfolioDetails.countryList || [];
      const cities = portfolioDetails.cityList || [];
      
      this.setState({
        selectedCountries: countries,
        selectAllCountries: countries.length > 0,
        selectedCities: cities,
        selectAllCities: cities.length > 0,
      });
      setSelectedPortfolioCountry(countries);
      setSelectedPortfolioCity(cities);

      callPortfolioData({
        userId,
        company: companyName ? [companyName] : [],
        country: ['all'],
        city: cities.length > 0 ? cities : ['all'],
        project: ['all'],
        portfolioLevel: 'city',
        type: 'all',
      });
    } else if (selectedLevel.toLowerCase() === 'project') {
      const countries = portfolioDetails.countryList || [];
      const cities = portfolioDetails.cityList || [];
      const projects = portfolioDetails.projectList || [];

      this.setState({
        selectedCountries: countries,
        selectAllCountries: countries.length > 0,
        selectedCities: cities,
        selectAllCities: cities.length > 0,
        selectedProjects: projects,
        selectAllProjects: projects.length > 0,
      });

      setSelectedPortfolioCountry(countries);
      setSelectedPortfolioCity(cities);
      setSelectedPortfolioProject(projects);

      callPortfolioData({
        userId,
        company: companyName ? [companyName] : [],
        country: countries.length > 0 ? countries : ['all'],
        city: cities.length > 0 ? cities : ['all'],
        project: projects.length > 0 ? projects : ['all'],
        portfolioLevel: 'project',
        type: 'all',
      });
    }
    // Extract station names and trigger statistics update
    // Use the selected year from props to match the current filter
    const stationNames = portfolioDetails?.stationDetails?.map(station => station.name) || [];
    if (stationNames.length > 0) {
        callPortfolioStatisticsData({
            stationNames,
            type: selectedPortfolioYear === 'all' ? 'all' : 'year',
            ...(selectedPortfolioYear !== 'all' && { time: selectedPortfolioYear }),
        });
    }
  };
  
  handleCompanyChange = async (event) => {
    const selectedCompany = event.target.value;
    const { 
      setSelectedPortfolioCompany,
      setSelectedPortfolioCountry,
      setSelectedPortfolioCity,
      setSelectedPortfolioProject,
      setSelectedPortfolioYear,
      setSelectedPortfolioStations,
      fetchPortfolioDetails,
      callPortfolioData,
      callPortfolioTotalize,  // Add callPortfolioTotalize
    } = this.props;
  
    const userId = getUserId();
  
    // Prevent redundant API calls
    this.setState({ isCompanyChanging: true });
  
    // Reset filters
    setSelectedPortfolioCompany(selectedCompany);
    setSelectedPortfolioCountry([]);
    setSelectedPortfolioCity([]);
    setSelectedPortfolioProject([]);
    setSelectedPortfolioYear('all');
    setSelectedPortfolioStations([]);
  
    // Call Portfolio Totalize first
    const totalizeRequest = {
      userId,
      company: selectedCompany,
    };
    await callPortfolioTotalize(totalizeRequest);  // Ensures totalize data is updated
  
    // Fetch updated portfolio details (countries, cities, projects)
    const portfolioRequest = {
      userId,
      company: [selectedCompany],
      country: ['all'],
      city: ['all'],
      project: ['all'],
    };
  
    const updatedDetails = await fetchPortfolioDetails(portfolioRequest);
    const updatedCountries = updatedDetails?.countryList || [];
  
    // Update state with new country list
    this.setState({
      selectedCountries: updatedCountries,
      selectAllCountries: updatedCountries.length > 0,
      isUserInteractingWithCountry: false, // <- Mark as not user-initiated
    });
  
    setSelectedPortfolioCountry(updatedCountries); // Update Redux
  
    // Now call the portfolio data API with the correct payload
    const requestData = {
      userId,
      company: [selectedCompany],
      country: updatedCountries.length > 0 ? updatedCountries : ['all'],
      city: ['all'],
      project: ['all'],
      portfolioLevel: 'country',
      type: 'all',
    };
  
    await callPortfolioData(requestData); // Ensure this completes before resetting
  
    // Reset flag after everything is done
    this.setState({ isCompanyChanging: false });
  };

  // Country Handlers
  handleCountryChange = (event) => {
    const { value, checked } = event.target;
    const { selectedCountries } = this.state;
  
    const updatedCountries = checked
      ? [...selectedCountries, value]
      : selectedCountries.filter((country) => country !== value);
  
    this.setState({
      selectedCountries: updatedCountries,
      selectAllCountries: updatedCountries.length === this.props.portfolioDetails.countryList.length,
      isUserInteractingWithCountry: true, 
    }, () => {
      // Force UI update after setting state
      this.forceUpdate();
    });
  
    this.props.setSelectedPortfolioCountry(updatedCountries);
  };
  
  handleSelectAllCountries = () => {
    const { selectAllCountries } = this.state;
    const countries = this.props.portfolioDetails.countryList || [];
    const cities = this.props.portfolioDetails.cityList || [];
    const projects = this.props.portfolioDetails.projectList || [];

    // Mark user interaction immediately
    this.setState({ isUserInteractingWithCountry: true });

    if (selectAllCountries) {
      // When unchecking "All" for countries
      this.setState({ 
        selectedCountries: [], 
        selectAllCountries: false,
        selectedCities: [], // Clear cities when unselecting all countries
        selectAllCities: false, // Uncheck "All" for cities
        selectedProjects: [], // Clear projects when unselecting all countries
        selectAllProjects: false // Uncheck "All" for projects
      });
      this.props.setSelectedPortfolioCountry([]);
      this.props.setSelectedPortfolioCity([]); // Clear cities in Redux
      this.props.setSelectedPortfolioProject([]); // Clear projects in Redux
      
      // Clear portfolio data and statistics when all countries are unchecked
      this.props.resetPortfolioData();
      this.props.resetPortfolioStatisticsData();

    } else {
      this.setState({ 
        selectedCountries: countries, 
        selectAllCountries: true,
        selectedCities: cities,          
        selectAllCities: true,
        selectedProjects: projects,     
        selectAllProjects: true
      });
      this.props.setSelectedPortfolioCountry(countries);
      this.props.setSelectedPortfolioCity(cities);   
      this.props.setSelectedPortfolioProject(projects); 
    }
  };  
  
// City Handlers
handleCityChange = (event) => {
  const { value, checked } = event.target;
  const { selectedCities } = this.state;

  const updatedCities = checked
    ? [...selectedCities, value]
    : selectedCities.filter((city) => city !== value);

  this.setState({
    selectedCities: updatedCities,
    selectAllCities: updatedCities.length === this.props.portfolioDetails.cityList.length,
  }, () => {
    // Force UI update after setting state
    this.forceUpdate();
  });

  this.props.setSelectedPortfolioCity(updatedCities);
  
  // Clear data if selection becomes empty and current level requires cities
  const { selectedPortfolioLevel } = this.state;
  if (updatedCities.length === 0 && (selectedPortfolioLevel === 'city' || selectedPortfolioLevel === 'project')) {
    this.props.resetPortfolioData();
    this.props.resetPortfolioStatisticsData();
  }
};

handleSelectAllCities = () => {
  const { selectAllCities } = this.state;
  const cities = this.props.portfolioDetails.cityList || [];
  const projects = this.props.portfolioDetails.projectList || [];

  if (selectAllCities) {
    // When unchecking "All" for cities
    this.setState({ 
      selectedCities: [], 
      selectAllCities: false,
      selectedProjects: [], // Clear projects when unselecting all cities
      selectAllProjects: false
     });
    this.props.setSelectedPortfolioCity([]);
    this.props.setSelectedPortfolioProject([]); // Clear projects in Redux
    
    // Clear portfolio data and statistics when all cities are unchecked
    // Only clear if current level requires cities (city or project level)
    const { selectedPortfolioLevel } = this.state;
    if (selectedPortfolioLevel === 'city' || selectedPortfolioLevel === 'project') {
      this.props.resetPortfolioData();
      this.props.resetPortfolioStatisticsData();
    }

  } else {
    // When checking "All" for cities
    this.setState({ 
      selectedCities: cities, 
      selectAllCities: true,
      selectedProjects: projects,      
      selectAllProjects: true
    });
    this.props.setSelectedPortfolioCity(cities);
    this.props.setSelectedPortfolioProject(projects); // Update Redux with all projects
  }
};

// Project Handlers
handleProjectChange = (event) => {
  const { value, checked } = event.target;
  const { selectedProjects } = this.state;
  const updatedProjects = checked
    ? [...selectedProjects, value]
    : selectedProjects.filter((project) => project !== value);

  this.setState({
    selectedProjects: updatedProjects,
    selectAllProjects: updatedProjects.length === this.props.portfolioDetails.projectList.length,
  });

  this.props.setSelectedPortfolioProject(updatedProjects);
  
  // Clear data if selection becomes empty and current level requires projects
  const { selectedPortfolioLevel } = this.state;
  if (updatedProjects.length === 0 && selectedPortfolioLevel === 'project') {
    this.props.resetPortfolioData();
    this.props.resetPortfolioStatisticsData();
  }
};

handleSelectAllProjects = () => {
  const { selectAllProjects } = this.state;
  const projects = this.props.portfolioDetails.projectList || [];

  if (selectAllProjects) {
    this.setState({ selectedProjects: [], selectAllProjects: false });
    this.props.setSelectedPortfolioProject([]);
    
    // Clear portfolio data and statistics when all projects are unchecked
    // Only clear if current level is project level
    const { selectedPortfolioLevel } = this.state;
    if (selectedPortfolioLevel === 'project') {
      this.props.resetPortfolioData();
      this.props.resetPortfolioStatisticsData();
    }
  } else {
    this.setState({ selectedProjects: projects, selectAllProjects: true });
    this.props.setSelectedPortfolioProject(projects);
  }
};

  handleYearChange = (event) => {
    const selectedYear = event.target.value;
    const { setSelectedPortfolioYear } = this.props;

    setSelectedPortfolioYear(selectedYear);
  };

  // handleSelectAllStations = () => {
  //   const { userRole } = this.props.portfolioDetails;
  //   const { portfolioDetails, setSelectedPortfolioStations, selectedPortfolioStations } = this.props;
  //   const allStations = portfolioDetails.stationList || [];

  //   if (selectedPortfolioStations.length === allStations.length) {
  //     setSelectedPortfolioStations([]);
  //   } else {
  //     setSelectedPortfolioStations(allStations);
  //   }
  // };

  handleStationSelection = (station) => {
    const { setSelectedPortfolioStations, selectedPortfolioStations } = this.props;

    if (selectedPortfolioStations.includes(station)) {
      setSelectedPortfolioStations(selectedPortfolioStations.filter((s) => s !== station));
    } else {
      setSelectedPortfolioStations([...selectedPortfolioStations, station]);
    }
  };

  isPortfolioDataInvalid = (data) => {
    // Check if portfolioData is null or any key has a null value
    if (!data) return true;
    return (
      data.company === null ||
      data.portfolio === null ||
      data.totalPortfolioCalculation === null ||
      data.stationStatisticsDetails === null
    );
  };

handleDownloadClick = async () => {
  const { 
      portfolioTotalize, 
      portfolioData, 
      portfolioStatisticsData, 
      portfolioDetails, 
      selectedPortfolioCompany, 
      selectedPortfolioYear 
  } = this.props;

  // Use updated state for portfolio level
  const { selectedPortfolioLevel } = this.state;

  console.log("Checking props in download click:", this.props);
  console.log("portfolioStatisticsData before download:", portfolioStatisticsData);

  if (!portfolioTotalize || !portfolioData) {
      console.warn("No portfolio data available for download.");
      return;
  }

  const fileType = selectedPortfolioYear === "all" ? "Yearly" : "Monthly";
  const csvRows = [];

  const stationNames = portfolioDetails.stationDetails?.map(station => station.name) || [];

  if (stationNames.length === 0) {
      console.warn("No stations selected for download.");
      return;
  }

  // Portfolio Totalize Data (Vertical Format)
  csvRows.push("Portfolio Totalize Data Current Year");
  csvRows.push(""); // Empty row for spacing

  const totalizeFields = [
      ["Company Name", portfolioTotalize.companyName],
      ["Total System Capacity (kWp)", portfolioTotalize.totalAnnualSystemCapacity],
      [`Total Annual Energy Yields (${getEnergyYieldUnit(portfolioTotalize.totalAnnualEnergyYields)})`,
        formatEnergyYield(portfolioTotalize.totalAnnualEnergyYields)],
      ["Average Annual Performance Ratio (%)", portfolioTotalize.avgAnnualPerformanceRatio]
  ];

  // Append each key-value pair in a new row
  totalizeFields.forEach(row => csvRows.push(formatCsvRow(row)));

  csvRows.push(""); // Empty row for separation

  // Portfolio Summary Data
  csvRows.push("Portfolio Summary Data");
  const uniqueYears = [...new Set(portfolioData.portfolioSummary.map(summary => summary.time))].sort();
  csvRows.push(["Date Time", ...uniqueYears].join(","));


  const summaryFields = [
      ["Total System Capacity (kWp)", ...uniqueYears.map(year => {
          const summary = portfolioData.portfolioSummary.find(summary => summary.time === year);
          return summary ? summary.totalSystemCapacity : "";
      })],
      ["Total Energy Yields (kWh)", ...uniqueYears.map(year => {
          const summary = portfolioData.portfolioSummary.find(summary => summary.time === year);
          return summary ? formatEnergyYield(summary.totalEnergyYields) : "";
      })],
      ["Average Performance Ratio (%)", ...uniqueYears.map(year => {
          const summary = portfolioData.portfolioSummary.find(summary => summary.time === year);
          return summary ? summary.avgPerformanceRatio : "";
      })]
  ];
  summaryFields.forEach(row => csvRows.push(formatCsvRow(row)));

  csvRows.push(""); 
  // Portfolio Details Data (Vertical Format)
  csvRows.push("Portfolio Details Data");
  csvRows.push(""); // Empty row for spacing

  // portfolio level 
  const selectedLevel = selectedPortfolioLevel ? selectedPortfolioLevel.toLowerCase() : "";

  csvRows.push(`Portfolio Level = '${selectedLevel}'`);

  csvRows.push(""); // Empty row for spacing

  // Get unique time periods from Portfolio Details
  const uniqueTimePeriods = [...new Set(portfolioData.portfolioDetails.map(detail => detail.time))].sort();

  // Generate column groups for proper arrangement
  const energyYieldHeaders = uniqueTimePeriods.map(time => `${time}_Energy_Yield(kWh)`);
  const prHeaders = uniqueTimePeriods.map(time => `${time}_PR(%)`);
  const weightagePRHeaders = uniqueTimePeriods.map(time => `${time}_Weightage_PR(%)`);

  // Construct the final ordered header row
  let headerRow = ["Country"];
  if (selectedLevel === "city") {
      headerRow.push("City");
  }
  if (selectedLevel === "project") {
      headerRow.push("Project");
  }
  headerRow.push("System Capacity (kWp)", ...energyYieldHeaders, ...prHeaders, ...weightagePRHeaders);
  csvRows.push(headerRow.join(","));

  // Group data by country, city, or project
  const groupedData = {};
  portfolioData.portfolioDetails.forEach(detail => {
      const key = selectedLevel === "country"
          ? detail.country
          : selectedLevel === "city"
          ? `${detail.country}_${detail.city}`
          : `${detail.country}_${detail.city}_${detail.project}`;

      if (!groupedData[key]) {
          groupedData[key] = {
              country: detail.country,
              city: detail.city || "",
              project: detail.project || "",
              systemCapacity: detail.systemCapacity,
              energyYields: {},
              performanceRatios: {},
              weightagePRs: {}
          };
      }

      groupedData[key].energyYields[detail.time] = detail.energyYields ?? "N/A";
      groupedData[key].performanceRatios[detail.time] = detail.performanceRatio || "N/A";
      groupedData[key].weightagePRs[detail.time] = detail.performanceRatioWeightage || "N/A";
  });

  // Generate rows for each country/city/project with reordered columns
  Object.values(groupedData).forEach(entry => {
      let row = [entry.country];
      if (selectedLevel === "city") {
          row.push(entry.city);
      }
      if (selectedLevel === "project") {
          row.push(entry.project);
      }
      row.push(entry.systemCapacity);

      // Add data in the new structured order
      uniqueTimePeriods.forEach(time => {
          const energyYield = entry.energyYields[time];
          row.push(energyYield == null || energyYield === "N/A" ? "N/A" : formatEnergyYield(energyYield));
      });
      uniqueTimePeriods.forEach(time => row.push(entry.performanceRatios[time] || "N/A"));
      uniqueTimePeriods.forEach(time => row.push(entry.weightagePRs[time] || "N/A"));

      csvRows.push(formatCsvRow(row));
  });

  csvRows.push(""); // Empty row for separation
  
// Portfolio Statistics Data (Formatted)
csvRows.push("Portfolio Statistics Data");
csvRows.push(""); // Empty row for spacing

// Extract unique time periods (years/months) from portfolioStatisticsData
const uniqueTimePeriodsStats = [...new Set(portfolioStatisticsData.map(stat => stat.time))].sort();

// Generate column headers dynamically
const energyYieldHeadersStats = uniqueTimePeriodsStats.map(time => `${time}_Energy_Yield (kWh)`);
const prHeadersStats = uniqueTimePeriodsStats.map(time => `${time}_PR (%)`);

// Construct the final ordered header row
let statisticsHeaderRow = [
    "Station Name",
    "Project Name",
    "System Capacity (kWp)",
    ...energyYieldHeadersStats,
    ...prHeadersStats
];
csvRows.push(statisticsHeaderRow.join(","));

// Grouping data by station
const groupedStatisticsData = {};
portfolioStatisticsData.forEach(stat => {
    const stationKey = `${stat.stationName} (${stat.stationDescription})`;

    if (!groupedStatisticsData[stationKey]) {
        groupedStatisticsData[stationKey] = {
            stationName: stationKey,
            projectName: stat.projectName,
            systemCapacity: stat.systemCapacity,
            energyYields: {},
            performanceRatios: {}
        };
    }

    groupedStatisticsData[stationKey].energyYields[stat.time] = stat.energyYields ?? "N/A";
    groupedStatisticsData[stationKey].performanceRatios[stat.time] = stat.performanceRatio || "N/A";
});

// **Sort stations by name**
const sortedStations = Object.keys(groupedStatisticsData).sort();

// Generate rows for each station in sorted order
sortedStations.forEach(stationKey => {
    const entry = groupedStatisticsData[stationKey];
    let row = [entry.stationName, entry.projectName, entry.systemCapacity];

    // Add data in the structured order
    uniqueTimePeriodsStats.forEach(time => {
        const energyYield = entry.energyYields[time];
        row.push(energyYield == null || energyYield === "N/A" ? "N/A" : formatEnergyYield(energyYield));
    });
    uniqueTimePeriodsStats.forEach(time => row.push(entry.performanceRatios[time] || "N/A"));

    csvRows.push(formatCsvRow(row));
});

csvRows.push(""); // Empty row for separation




  // Convert CSV data to Blob
  const csvData = '\uFEFF' + csvRows.join("\n"); 
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

  // Create download link
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", `${selectedPortfolioCompany || "Portfolio"}_${fileType}_Statistics.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  render() {

    const { selectedPortfolioLevel, selectedCountries, selectAllCountries } = this.state;

    const {
      classes,
      portfolioLevels,
      portfolioDetails,
      portfolioData,
      selectedPortfolioCompany,
      selectedPortfolioCity,
      selectedPortfolioCountry,
      selectedPortfolioProject,
      selectedPortfolioYear,
      selectedPortfolioStations,
    } = this.props;

    const companies = portfolioDetails.companyList || [];
    const countries = portfolioDetails.countryList || [];
    const cities = portfolioDetails.cityList || [];
    const projects = portfolioDetails.projectList || [];
    const years = portfolioDetails.yearList || [];
    const stations = portfolioDetails.stationList || [];

    const isAdmin = portfolioDetails.userRole === 'admin';

    const { showCountry, showCity, showProject } = this.getVisibleCheckboxes();

    return (
      <div className={classes.form}>
        <Grid container alignItems="center">
          {/* Company Dropdown */}
          <Grid item xs={12}>
            <FormControl className={classes.formControlWrapper} variant="outlined" fullWidth>
              <InputLabel htmlFor="company-select">Company</InputLabel>
              <Select
                value={selectedPortfolioCompany}
                onChange={this.handleCompanyChange}
                input={
                  <OutlinedInput
                    labelWidth={70}
                    name="Company"
                    id="company-select"
                    className={classes.formControlWrapperSelectMenu}
                  />
                }
                disabled={!isAdmin} // Disable for non-admin users
              >
                {companies.map((company, index) => (
                  <MenuItem key={index} value={company}>
                    {company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Portfolio Level Dropdown */}
          <Grid item xs={12}>
            <FormControl className={classes.formControlWrapper} variant="outlined" fullWidth>
              <InputLabel htmlFor="portfolio-level-select">Portfolio Selection</InputLabel>
              <Select
                value={selectedPortfolioLevel.toLowerCase()} // Ensure it's always lowercase
                onChange={this.handlePortfolioLevelChange}
                input={
                  <OutlinedInput
                    labelWidth={135}
                    name="Portfolio Level"
                    id="portfolio-level-select"
                    className={classes.formControlWrapperSelectMenu}
                  />
                }
              >
                {portfolioLevels.map((level) => (
                  <MenuItem key={level.portfolioLevelId} value={level.portfolioSelection.toLowerCase()}>
                    {level.portfolioSelection}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Year Dropdown */}
          {years.length > 0 && (
            <Grid item xs={12}>
              <FormControl className={classes.formControlWrapper} variant="outlined" fullWidth>
                <InputLabel htmlFor="year-select">Year</InputLabel>
                <Select
                  value={selectedPortfolioYear}
                  onChange={this.handleYearChange}
                  input={
                    <OutlinedInput
                      labelWidth={35}
                      name="Year"
                      id="year"
                      key="year-select"
                      className={classes.formControlWrapperSelectMenu}
                    />
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  {years.map((year, index) => (
                    <MenuItem key={index} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {/* Country Checkbox Group */}
          <Grid item xs={12} className={classes.scrollContainer}>

          {showCountry && (
            <Grid item xs={12}>
              <FormControl component="fieldset" className={classes.formControlWrapperCheckbox}>
                <FormLabel component="legend" className={classes.CheckboxFormLabelCountry}>Country</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.selectAllCountries}
                        onChange={this.handleSelectAllCountries}
                        color="primary"
                      />
                    }
                    label="All"
                  />
                  {countries.map((country, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          value={country}
                          checked={this.state.selectedCountries.includes(country)}
                          onChange={this.handleCountryChange}
                          color="primary"
                        />
                      }
                      label={country}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          )}

          {/* City Checkbox Group */}
          {showCity && (
            <Grid item xs={12}>
              <FormControl component="fieldset" className={classes.formControlWrapperCheckbox}>
                <FormLabel component="legend" className={classes.CheckboxFormLabelCity}>City</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.selectAllCities}
                        onChange={this.handleSelectAllCities}
                        color="primary"
                      />
                    }
                    label="All"
                  />
                  {cities.map((city, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          value={city}
                          checked={this.state.selectedCities.includes(city)}
                          onChange={this.handleCityChange}
                          color="primary"
                        />
                      }
                      label={city}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          )}

          {/* Project Checkbox Group */}
          {showProject && (
            <Grid item xs={12}>
              <FormControl component="fieldset" className={classes.formControlWrapperCheckbox}>
                <FormLabel component="legend" className={classes.CheckboxFormLabelProject}>Project</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.selectAllProjects}
                        onChange={this.handleSelectAllProjects}
                        color="primary"
                      />
                    }
                    label="All"
                  />
                  {projects.map((project, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          value={project}
                          checked={this.state.selectedProjects.includes(project)}
                          onChange={this.handleProjectChange}
                          color="primary"
                        />
                      }
                      label={project}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          )}
          

          {/* Stations Checkbox */}
          {/* {stations.length > 0 && (
            <Grid item xs={12}>
              <FormControl component="fieldset" className={classes.formControlWrapperCheckbox}>
                <FormLabel component="legend">Stations</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="selectAll"
                        color="primary"
                        checked={selectedPortfolioStations.length === stations.length && stations.length > 0}
                        onChange={this.handleSelectAllStations}
                      />
                    }
                    label="All"
                  />
                  {stations.map((station) => (
                    <FormControlLabel
                      key={station}
                      control={
                        <Checkbox
                          value={station}
                          color="primary"
                          checked={selectedPortfolioStations.includes(station)}
                          onChange={() => this.handleStationSelection(station)}
                        />
                      }
                      label={station}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
          )} */}
          {/* Download Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              disabled={!this.isDownloadReady()}
              onClick={this.handleDownloadClick}
            >
              Download
            </Button>
          </Grid>
          </Grid>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  portfolioLevels: statisticsReducers.getPortfolioLevels(state),
  portfolioData: statisticsReducers.getPortfolioData(state),
  portfolioStatisticsData: statisticsReducers.getPortfolioStatisticsData(state),  
  portfolioDetails: statisticsReducers.getPortfolioDetails(state),
  portfolioTotalize: statisticsReducers.getPortfolioTotalize(state),
  selectedPortfolioLevel: statisticsReducers.getSelectedPortfolioLevel(state),
  selectedPortfolioCompany: statisticsReducers.getSelectedPortfolioCompany(state),
  selectedPortfolioCity: statisticsReducers.getSelectedPortfolioCity(state),
  selectedPortfolioCountry: statisticsReducers.getSelectedPortfolioCountry(state),
  selectedPortfolioProject: statisticsReducers.getSelectedPortfolioProject(state),
  selectedPortfolioYear: statisticsReducers.getSelectedPortfolioYear(state),
});

const mapActionsToProps = {
  callPortfolioData: statisticsActionMgr.callPortfolioData, 
  callPortfolioStatisticsData: statisticsActionMgr.callPortfolioStatisticsData, 
  callPortfolioTotalize: statisticsActionMgr.callPortfolioTotalize,
  fetchPortfolioLevels: statisticsActionMgr.fetchPortfolioLevels,
  fetchPortfolioDetails: statisticsActionMgr.fetchPortfolioDetails,
  setSelectedPortfolioCompany: statisticsActionMgr.setSelectedPortfolioCompany,
  setSelectedPortfolioCity: statisticsActionMgr.setSelectedPortfolioCity,
  setSelectedPortfolioCountry: statisticsActionMgr.setSelectedPortfolioCountry,
  setSelectedPortfolioProject: statisticsActionMgr.setSelectedPortfolioProject,
  setSelectedPortfolioYear: statisticsActionMgr.setSelectedPortfolioYear,
  setSelectedPortfolioStations: statisticsActionMgr.setSelectedPortfolioStations,
  resetPortfolioData: statisticsActionMgr.resetPortfolioData,
  resetPortfolioStatisticsData: statisticsActionMgr.resetPortfolioStatisticsData,
};

export default connect(mapStateToProps, mapActionsToProps)(withStyles(styles)(StationStatisticsPortfolioForm));
